import { apiClient } from './../client';
import { API_ENDPOINTS } from './../constant/endpoints';
import type { IApiResponse, IPaginatedResponse } from './../types';
import type {
    IContact,
    IContactListResult,
    ITag,
    IContactQuery,
    ICreateContact,
    IUpdateContact,
    IUpdateContactStage,
    IMarkWonInput,
    ICreateEstimateInput,
    IUpdateEstimateInput,
    IEstimate,
    IBulkDelete,
    IBulkEdit,
} from '@/features/contacts/types';
import type {
    IContactActivityQuery,
    IContactActivityResponse,
} from '@/features/contacts/types/contact-activity.types';
import type {
    IContactNotesQuery,
    IContactNotesResponse,
    ICreateContactNote,
} from '@/features/contacts/types/contact-notes.types';
import type {
    IContactEmailsQuery,
    IContactEmailsResult,
} from '@/features/contacts/types/contact-email.types';
import type {
    IContactEmailSubscription,
    IUpdateContactEmailSubscription,
} from '@/features/contacts/types/contact-email-subscription.types';
import { toStatusFilterParam } from './status-filter-param';
import type { IContactLinkAnalytics } from '@/features/contacts/types/contact-link-analytics.types';
import type {
    IContactFormSubmission,
    IContactFormSubmissionsQuery,
    IContactFormSubmissionsResult,
} from '@/features/contacts/types/contact-form-submission.types';

export const contactsApi = {
    /**
     * Contacts CSV for the CURRENT filters — the server streams it, so there is
     * no row cap and nothing is truncated.
     *
     * Takes the same `params` the list does (minus paging, which the server
     * ignores here) so the file matches what the operator is looking at.
     * `customFieldFilters` is JSON-encoded for the same reason as in `list`.
     */
    exportCsv: (params?: IContactQuery): Promise<Blob> =>
        apiClient
            .get<Blob>(API_ENDPOINTS.CONTACTS.EXPORT, {
                responseType: 'blob',
                params: {
                    ...params,
                    customFieldFilters: params?.customFieldFilters?.length
                        ? JSON.stringify(params.customFieldFilters)
                        : undefined,
                },
            })
            .then((r) => r.data),

    list: (params?: IContactQuery): Promise<IContactListResult> =>
        apiClient
            .get<IApiResponse<IContact[]> & { meta: IPaginatedResponse<IContact> }>(
                API_ENDPOINTS.CONTACTS.LIST,
                {
                    params: {
                        ...params,
                        // The backend expects a JSON-encoded array; axios would
                        // otherwise serialize object arrays as bracket notation.
                        customFieldFilters: params?.customFieldFilters?.length
                            ? JSON.stringify(params.customFieldFilters)
                            : undefined,
                    },
                },
            )
            .then((r) => {
                const meta = r.data.meta as IPaginatedResponse<IContact> & {
                    availableTags?: readonly ITag[];
                };
                return {
                    data: r.data.data,
                    page: meta.page,
                    limit: meta.limit,
                    total: meta.total,
                    totalPages: meta.totalPages,
                    availableTags: meta.availableTags ?? [],
                };
            }),

    getAllIds: (params: IContactQuery) =>
        apiClient
            .get<IApiResponse<string[]>>(API_ENDPOINTS.CONTACTS.IDS, {
                params: {
                    ...params,
                    page: undefined,
                    limit: undefined,
                    // Match `list()` — the backend parses a JSON-encoded array.
                    customFieldFilters: params.customFieldFilters?.length
                        ? JSON.stringify(params.customFieldFilters)
                        : undefined,
                },
            })
            .then((r) => r.data.data),

    getById: (id: string) =>
        apiClient
            .get<IApiResponse<IContact>>(API_ENDPOINTS.CONTACTS.BY_ID(id))
            .then((r) => r.data.data),

    create: (data: ICreateContact) =>
        apiClient
            .post<IApiResponse<IContact>>(API_ENDPOINTS.CONTACTS.LIST, data)
            .then((r) => r.data.data),

    update: (id: string, data: IUpdateContact) =>
        apiClient
            .patch<IApiResponse<IContact>>(API_ENDPOINTS.CONTACTS.BY_ID(id), data)
            .then((r) => r.data.data),

    updateStage: (id: string, data: IUpdateContactStage) =>
        apiClient
            .patch<IApiResponse<IContact>>(API_ENDPOINTS.CONTACTS.STAGE(id), data)
            .then((r) => r.data.data),

    markWon: (id: string, data: IMarkWonInput) =>
        apiClient
            .post<IApiResponse<{ conversionId: string }>>(API_ENDPOINTS.CONTACTS.MARK_WON(id), data)
            .then((r) => r.data.data),

    createEstimate: (id: string, data: ICreateEstimateInput) =>
        apiClient
            .post<IApiResponse<IEstimate>>(API_ENDPOINTS.CONTACTS.ESTIMATES(id), data)
            .then((r) => r.data.data),

    updateEstimate: (id: string, estimateId: string, data: IUpdateEstimateInput) =>
        apiClient
            .patch<IApiResponse<IEstimate>>(API_ENDPOINTS.CONTACTS.ESTIMATE(id, estimateId), data)
            .then((r) => r.data.data),

    /** Puts back what the AI decided about this lead (within 7 days). */
    undoDecision: (id: string) =>
        apiClient
            .post<IApiResponse<null>>(API_ENDPOINTS.CONTACTS.UNDO_DECISION(id))
            .then((r) => r.data.data),

    remove: (id: string) =>
        apiClient
            .delete<IApiResponse<null>>(API_ENDPOINTS.CONTACTS.BY_ID(id))
            .then((r) => r.data.data),

    bulkDelete: (data: IBulkDelete) =>
        apiClient
            .post<IApiResponse<null>>(API_ENDPOINTS.CONTACTS.BULK_DELETE, data)
            .then((r) => r.data.data),

    bulkEdit: (data: IBulkEdit) =>
        apiClient
            .post<IApiResponse<null>>(API_ENDPOINTS.CONTACTS.BULK_EDIT, data)
            .then((r) => r.data.data),

    addTags: (id: string, tagIds: readonly string[]) =>
        apiClient
            .post<IApiResponse<IContact>>(API_ENDPOINTS.CONTACTS.TAGS(id), { tagIds })
            .then((r) => r.data.data),

    removeTag: (id: string, tagId: string) =>
        apiClient
            .delete<IApiResponse<IContact>>(API_ENDPOINTS.CONTACTS.TAG_BY_ID(id, tagId))
            .then((r) => r.data.data),

    getActivity: (id: string, params?: IContactActivityQuery) =>
        apiClient
            .get<IApiResponse<IContactActivityResponse>>(API_ENDPOINTS.CONTACTS.ACTIVITY(id), {
                params,
            })
            .then((r) => r.data.data),

    getNotes: (id: string, params?: IContactNotesQuery) =>
        apiClient
            .get<IApiResponse<IContactNotesResponse>>(API_ENDPOINTS.CONTACTS.NOTES(id), { params })
            .then((r) => r.data.data),

    createNote: (id: string, data: ICreateContactNote) =>
        apiClient
            .post<IApiResponse<{ id: string }>>(API_ENDPOINTS.CONTACTS.NOTES(id), data)
            .then((r) => r.data.data),

    deleteNote: (id: string, noteId: string) =>
        apiClient
            .delete<IApiResponse<null>>(API_ENDPOINTS.CONTACTS.NOTE_BY_ID(id, noteId))
            .then((r) => r.data.data),

    /** Whether the contact's email address is subscribed (not on the suppression list). */
    getEmailSubscription: (id: string) =>
        apiClient
            .get<
                IApiResponse<IContactEmailSubscription>
            >(API_ENDPOINTS.CONTACTS.EMAIL_SUBSCRIPTION(id))
            .then((r) => r.data.data),

    /** 409 when re-subscribing a contact who unsubscribed themselves. */
    updateEmailSubscription: (id: string, data: IUpdateContactEmailSubscription) =>
        apiClient
            .patch<IApiResponse<null>>(API_ENDPOINTS.CONTACTS.EMAIL_SUBSCRIPTION(id), data)
            .then((r) => r.data.data),

    /** Email History tab — status/search narrowing is applied server-side. */
    listEmails: (id: string, params?: IContactEmailsQuery) =>
        apiClient
            .get<IApiResponse<IContactEmailsResult>>(API_ENDPOINTS.CONTACTS.EMAILS(id), {
                params: {
                    statuses: toStatusFilterParam(params?.statuses),
                    q: params?.q ? params.q : undefined,
                    page: params?.page,
                },
            })
            .then((r) => r.data.data),

    getLinkAnalytics: (id: string) =>
        apiClient
            .get<IApiResponse<IContactLinkAnalytics>>(API_ENDPOINTS.CONTACTS.LINK_ANALYTICS(id))
            .then((r) => r.data.data),

    /** Lead forms this contact has submitted — the contact-detail Forms tab. */
    listFormSubmissions: (
        id: string,
        params?: IContactFormSubmissionsQuery,
    ): Promise<IContactFormSubmissionsResult> =>
        apiClient
            .get<
                IApiResponse<readonly IContactFormSubmission[]> & {
                    meta: IPaginatedResponse<IContactFormSubmission>;
                }
            >(API_ENDPOINTS.CONTACTS.FORMS(id), { params })
            .then((r) => ({
                items: r.data.data,
                total: r.data.meta.total,
                hasMore: r.data.meta.page < r.data.meta.totalPages,
            })),
};
